<template>
  <el-config-provider namespace="es">
    <el-dialog
      :title="t('feedback')"
      v-model="dialogVisible"
      :close-on-click-modal="false"
      @closed="handleDialogClosed"
    >
      <el-form
        class="feedback-form"
        ref="formRef"
        :model="form"
        :rules="rules"
        label-width="auto"
      >
        <el-form-item :label="t('title')" prop="title">
          <el-input v-model="form.title" :placeholder="t('pleaseInput')" />
        </el-form-item>

        <el-form-item :label="t('desc')" prop="content">
          <div id="sentryRichTextEditorWrapper" style="width: 100%">
            <div id="sentryRichTextEditor"></div>
          </div>
        </el-form-item>

        <el-form-item :label="t('attachments')" prop="attachments">
          <el-upload
            v-model:file-list="form.attachments"
            :action="uploadAction"
            :headers="uploadHeaders"
            :on-preview="handlePictureCardPreview"
            :on-success="handleUploadSuccess"
            :on-error="handleUploadError"
            multiple
          >
            <el-button type="primary">{{ t('upload') }}</el-button>
          </el-upload>
        </el-form-item>
      </el-form>

      <template #footer>
        <div class="dialog-footer">
          <el-button type="primary" :loading="submitting" @click="handleSubmit">
            {{ t('submit') }}
          </el-button>
          <el-button @click="dialogVisible = false">
            {{ t('cancel') }}
          </el-button>
        </div>
      </template>
    </el-dialog>

    <el-image-viewer
      v-if="previewVisible"
      show-progress
      :url-list="previewList"
      @close="previewVisible = false"
    />
  </el-config-provider>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { ElMessage } from 'element-plus'
import { FloatingMenu } from './plugins/floatingMenu'
import { feedbackEvent, getSentry, TwyfordSentry } from './core/init'
import {
  focusedBlur,
  loadCSS,
  getCdnUrl,
  formatLanguage
} from '@/utils/helpers'
import { loadQuill, initQuill } from '@/utils/quill'
import { t } from '@/locales'
import { getSentryUrl } from './config'
import 'element-plus/es/components/message/style/css'
import { getLocale } from '@/locales'

// ==================== 常量定义 ====================
const UPLOAD_HEADERS = {
  'tf-sentry-key': import.meta.env.VITE_APP_SENTRY_API_KEY
}

const IMAGE_EXTENSIONS = /\.(jpg|jpeg|png|gif|webp)$/i

// ==================== 响应式状态 ====================
const dialogVisible = ref(false)
const submitting = ref(false)
const formRef = ref(null)
const floatingMenu = ref(null)
const lastEvent = ref({})
const previewVisible = ref(false)
const previewImageUrl = ref('')

// Quill 编辑器实例
let quillInstance = null

// ==================== 表单相关 ====================
const createEmptyForm = (params = {}) => ({
  title: '',
  content: '',
  attachments: [],
  eventId: '',
  replayId: '',
  ...params
})

const form = ref(createEmptyForm())

// 自定义内容验证器
const validateContent = (rule, value, callback) => {
  if (!quillInstance) {
    return callback(new Error(t('pleaseInput')))
  }

  const content = quillInstance.getText().trim().replace(/\n/g, '')
  if (!content) {
    return callback(new Error(t('pleaseInput')))
  }
  callback()
}

const rules = computed(() => ({
  title: [{ required: true, message: t('pleaseInput'), trigger: 'blur' }],
  content: [
    {
      required: true,
      validator: validateContent,
      trigger: 'change'
    }
  ]
}))

// ==================== 计算属性 ====================
const uploadAction = computed(() => `${getSentryUrl()}/upload`)

const uploadHeaders = computed(() => UPLOAD_HEADERS)

const previewList = computed(() =>
  previewImageUrl.value ? [previewImageUrl.value] : []
)

// ==================== Quill 编辑器 ====================
const initQuillEditor = async () => {
  if (quillInstance) return quillInstance

  try {
    quillInstance = await initQuill({
      getQuill: () => quillInstance,
      id: '#sentryRichTextEditor'
    })

    // 监听内容变化，清除验证错误
    quillInstance.on('text-change', () => {
      formRef.value?.clearValidate('content')
    })

    return quillInstance
  } catch (error) {
    console.error('初始化 Quill 编辑器失败:', error)
    ElMessage.error('编辑器初始化失败')
    return null
  }
}

// ==================== 表单操作 ====================
const resetForm = (params = {}) => {
  form.value = createEmptyForm(params)

  // 清空编辑器内容
  if (quillInstance) {
    quillInstance.setText('')
  }

  // 清除验证
  nextTick(() => {
    formRef.value?.clearValidate()
  })
}

const getCurrentUser = () => {
  try {
    const sentry = getSentry()
    return sentry?.getIsolationScope().getUser() || {}
  } catch (error) {
    console.error('获取当前用户失败:', error)
    return {}
  }
}

const buildFeedbackData = (user) => {
  const { eventId, replayId } = form.value

  return {
    empNo: user.id || '',
    name: user.username || 'no name',
    content: quillInstance.getSemanticHTML() || '',
    system: user.system || '',
    title: form.value.title || '',
    attachments: form.value.attachments.map((item) => ({
      url: item.url,
      filename: item.name
    })),
    eventId,
    replayId,
    language: formatLanguage(getLocale())
  }
}

const reportToSentry = (feedbackData) => {
  const sentry = getSentry()
  if (!sentry) return

  const { eventId, attachments, ...sentryData } = feedbackData
  sentryData.message = quillInstance.getText() || ''

  sentry.captureFeedback(sentryData, {
    captureContext: {
      tags: {
        linked_error_event_id: eventId || 'none',
        feedback_source: 'user_feedback_modal'
      },
      contexts: eventId
        ? {
            linked_error: { event_id: eventId }
          }
        : undefined
    },
    attachments: attachments.map(({ filename, url }) => ({
      filename,
      data: url
    }))
  })
}

const submitToServer = async (feedbackData) => {
  const response = await fetch(`${getSentryUrl()}/feedback`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...UPLOAD_HEADERS
    },
    body: JSON.stringify(feedbackData)
  })

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`)
  }

  return response.json()
}

const handleSubmit = async () => {
  if (submitting.value) return

  try {
    const valid = await formRef.value?.validate()
    if (!valid) return

    submitting.value = true

    const user = getCurrentUser()

    if (!user.id) {
      ElMessage.error({
        message: t('loginFirst'),
        customClass: 'g-high-zindex-message'
      })
      return
    }

    const feedbackData = buildFeedbackData(user)

    // 上报到 Sentry
    reportToSentry(feedbackData)

    // 提交工单
    const result = await submitToServer(feedbackData)

    if (result.success) {
      ElMessage.success(result.message || t('submitSucess'))
      dialogVisible.value = false
      floatingMenu.value?.setBadgeCount(0)
    } else {
      ElMessage.error({
        message: result.message || t('submitFailed'),
        customClass: 'g-high-zindex-message'
      })
    }
  } catch (error) {
    console.error('提交反馈失败:', error)
    ElMessage.error({
      message: t('submitFailed'),
      customClass: 'g-high-zindex-message'
    })
  } finally {
    submitting.value = false
  }
}

// ==================== 文件上传 ====================
const isImageFile = (url) => IMAGE_EXTENSIONS.test(url)

const handlePictureCardPreview = (uploadFile) => {
  focusedBlur()

  if (isImageFile(uploadFile.url)) {
    previewImageUrl.value = uploadFile.url
    previewVisible.value = true
  } else {
    window.open(uploadFile.url, '_blank')
  }
}

const handleUploadSuccess = (response, uploadFile) => {
  if (!response?.data) {
    ElMessage.error('上传响应数据异常')
    return
  }

  const fileIndex = form.value.attachments.findIndex(
    (item) => item.uid === uploadFile.uid
  )

  const newFile = {
    name: response.data.fileName,
    url: response.data.url,
    uid: uploadFile.uid
  }

  if (fileIndex >= 0) {
    form.value.attachments.splice(fileIndex, 1, newFile)
  } else {
    form.value.attachments.push(newFile)
  }
}

const handleUploadError = (error) => {
  console.error('上传失败:', error)
  ElMessage.error(t('uploadFailed') || '上传失败')
}

// ==================== 对话框操作 ====================
const openFeedbackDialog = async (params = {}) => {
  // 合并最后一次错误事件的信息
  if (lastEvent.value.eventId) {
    params = {
      eventId: lastEvent.value.eventId,
      replayId: lastEvent.value.replayId,
      ...params
    }
  }

  dialogVisible.value = true
  await initQuillEditor()
  resetForm(params)
}

const handleDialogClosed = () => {
  resetForm()
  submitting.value = false
}

// ==================== 浮动菜单 ====================
const shouldShowBadge = () => {
  const config = TwyfordSentry.getConfig()
  return config?.showBadge ?? true
}

const initFloatingMenu = () => {
  floatingMenu.value = new FloatingMenu({
    onItemClick: (item) => {
      if (dialogVisible.value) return

      if (item.action === 'feedback') {
        openFeedbackDialog()
      }
    }
  })

  TwyfordSentry.setFloatingMenu(floatingMenu.value)
}

// ==================== 事件监听 ====================
const handleFeedbackEvent = (e) => {
  if (dialogVisible.value) return

  try {
    const eventId = e.detail?.event?.event_id
    const replayId = e.detail?.event?.tags?.replayId

    if (eventId) {
      lastEvent.value = { eventId, replayId }

      if (shouldShowBadge()) {
        floatingMenu.value?.addBadgeCount()
      }

      const config = TwyfordSentry.getConfig()
      if (config?.enableAutoReport) {
        openFeedbackDialog({ eventId, replayId })
      }
    }
  } catch (error) {
    console.error('处理反馈事件失败:', error)
  }
}

// ==================== 生命周期 ====================
onMounted(() => {
  initFloatingMenu()
  loadQuill() // 预加载 Quill 资源
  window.addEventListener(feedbackEvent, handleFeedbackEvent)
  loadCSS(getCdnUrl('/sentry-plugin.css', { version: true }))
})

onBeforeUnmount(() => {
  window.removeEventListener(feedbackEvent, handleFeedbackEvent)

  // 清理 Quill 实例
  if (quillInstance) {
    quillInstance = null
  }
})
</script>

<style lang="scss">
@import './style.css';

/* 全局布局样式（仅作用于本组件） */
#sentryRichTextEditorWrapper {
  .ql-snow .ql-picker.ql-header .ql-picker-label::before,
  .ql-snow .ql-picker.ql-header .ql-picker-item::before {
    vertical-align: top;
  }
  .ql-editor {
    min-height: 150px;
    max-height: 300px;
    overflow-y: auto;
  }
}

.feedback-form {
  .el-form-item__content {
    height: fit-content;
  }
  .el-upload__input {
    display: none !important;
  }
}

.g-high-zindex-message {
  z-index: 20000 !important;
}
</style>
